import React, { useState, useEffect, useCallback } from 'react';
import alphabetData from '../Data/alphabetData';
import Confetti from 'react-confetti';
import * as FaIcons from 'react-icons/fa';
import * as GiIcons from 'react-icons/gi';
import * as MdIcons from 'react-icons/md';
import * as BsIcons from 'react-icons/bs';
import { shuffle } from '../utils/shuffle';
import { getRandomMascotQuote } from '../utils/mascotQuotes';
import useAudio from '../hooks/useAudio';
import MascotCelebration from './MascotCelebration';
import ScoreBadge from './ScoreBadge';

const iconMap = { ...FaIcons, ...GiIcons, ...MdIcons, ...BsIcons };

const MemoryGame = ({ onClose }) => {
  const [cards, setCards] = useState([]);
  const [flippedCards, setFlippedCards] = useState([]);
  const [matchedPairs, setMatchedPairs] = useState([]);
  const [score, setScore] = useState(0);
  const [moves, setMoves] = useState(0);
  const [showConfetti, setShowConfetti] = useState(false);
  const [showMascot, setShowMascot] = useState(false);
  const [mascotQuote, setMascotQuote] = useState('');
  const { play, preload } = useAudio();

  const initializeGame = useCallback(() => {
    const allWords = alphabetData.flatMap(letter =>
      letter.words
        .filter(word => word.icon && word.games?.includes('MemoryGame'))
        .map(word => ({
          word: word.word,
          disjointed: [...word.word].join(' '),
          icon: word.icon || null,
          soundFile: word.soundFile || null
        }))
    );

    const selected = shuffle(allWords).slice(0, 6);

    const pairedCards = selected.flatMap(item => [
      {
        type: 'cue',
        word: item.word,
        id: `${item.word}-cue`,
        icon: item.icon,
        text: item.disjointed,
        soundFile: item.soundFile
      },
      {
        type: 'answer',
        word: item.word,
        id: `${item.word}-answer`,
        text: item.word,
        soundFile: item.soundFile
      }
    ]);

    setCards(shuffle(pairedCards));
    setFlippedCards([]);
    setMatchedPairs([]);
    setScore(0);
    setMoves(0);
    setShowMascot(false);
    setMascotQuote('');
    setShowConfetti(false);

    preload(selected.map(item => item.soundFile));
  }, [preload]);

  useEffect(() => {
    initializeGame();
  }, [initializeGame]);

  const triggerCelebration = useCallback(() => {
    setMascotQuote(getRandomMascotQuote());
    setTimeout(() => {
      setShowMascot(true);
      setShowConfetti(true);
      play('fanfare.mp3', { interrupt: false });
    }, 500);
    setTimeout(() => setShowConfetti(false), 5000);
  }, [play]);

  useEffect(() => {
    if (matchedPairs.length > 0 && matchedPairs.length === cards.length) {
      triggerCelebration();
    }
  }, [matchedPairs, cards.length, triggerCelebration]);

  const handleClick = (card) => {
    if (flippedCards.length === 2 || flippedCards.some(c => c.id === card.id)) return;

    if (card.type === 'cue' && card.soundFile) {
      play(card.soundFile);
    }

    const newFlipped = [...flippedCards, card];
    setFlippedCards(newFlipped);

    if (newFlipped.length === 2) {
      setMoves(prev => prev + 1);
      if (
        newFlipped[0].word === newFlipped[1].word &&
        newFlipped[0].type !== newFlipped[1].type
      ) {
        const newMatched = [...matchedPairs, newFlipped[0].id, newFlipped[1].id];
        setMatchedPairs(newMatched);
        setScore(prev => prev + 1);

        if (card.soundFile) play(card.soundFile);
        play('correct.mp3', { interrupt: false });
      }
      setTimeout(() => setFlippedCards([]), 1000);
    }
  };

  const isFlipped = (card) =>
    flippedCards.some(c => c.id === card.id) || matchedPairs.includes(card.id);

  const renderCard = (card) => {
    const flipped = isFlipped(card);
    return (
      <div
        key={card.id}
        className={`memory-card ${flipped ? 'flipped' : ''}`}
        onClick={() => handleClick(card)}
      >
        {flipped && (
          <>
            {card.type === 'cue' ? (
              <div className="cue-card">
                {card.icon && iconMap[card.icon] && (
                  <div className="cue-icon">
                    {React.createElement(iconMap[card.icon], { size: 36 })}
                  </div>
                )}
                <div className="cue-text">{card.text}</div>
              </div>
            ) : (
              <div className="card-text">{card.text}</div>
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <div className="game-popup-content" onClick={(e) => e.stopPropagation()}>
      <h2 className="memory-title">یادداشت کا کھیل</h2>

      <ScoreBadge score={score} stats={[{ label: 'چالیں', value: moves }]} />

      <div className="memory-grid">
        {cards.map(renderCard)}
      </div>

      {showMascot && (
        <MascotCelebration quote={mascotQuote} onReplay={initializeGame} />
      )}

      {showConfetti && <Confetti />}
    </div>
  );
};

export default MemoryGame;
